import { useEffect } from "react"
import type { ReactNode, RefObject } from "react"
import { CloseIcon } from "components/ui/icons/uploadbar/CloseIcon"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { FooterBar } from "components/ui/customWebviewer/FooterBar/FooterBar"
import style from "./CreateAreaSidebar.module.scss"

type Props = {
	title: string
	hint: string
	area: { pageNumber: number } | null
	isPending?: boolean
	canSubmit: boolean
	submitLabel: string
	onCancel: () => void
	onClose: () => void
	onSubmit: () => void
	focusTargetRef?: RefObject<HTMLElement>
	error?: string
	children: ReactNode
	sidebarDataAttribute?: "data-elevation-callout-sidebar"
	selectFocusTargetText?: boolean
}

/** Shared canvas-area creation panel shell. Concrete forms provide only their fields. */
export const CreateAreaSidebar = ({
	title,
	hint,
	area,
	isPending = false,
	canSubmit,
	submitLabel,
	onCancel,
	onClose,
	onSubmit,
	focusTargetRef,
	error,
	children,
	sidebarDataAttribute,
	selectFocusTargetText = false,
}: Props) => {
	useEffect(() => {
		if (!area || !focusTargetRef?.current) return
		const raf = requestAnimationFrame(() => {
			const input = focusTargetRef.current?.querySelector<HTMLInputElement>("input")
			input?.focus()
			if (selectFocusTargetText) input?.select()
		})
		return () => cancelAnimationFrame(raf)
	}, [area, focusTargetRef, selectFocusTargetText])

	return (
		<div
			className={style.sidebar}
			data-hotkey-sidebar
			tabIndex={-1}
			{...(sidebarDataAttribute ? { [sidebarDataAttribute]: true } : {})}
		>
			<div className={style.header} data-hotkey-skip>
				<span>{title}</span>
				<button className={style.closeButton} onClick={onClose} aria-label="Close sidebar">
					<CloseIcon />
				</button>
			</div>
			<div className={style.content}>
				{area ? children : <p className={style.hint}>{hint}</p>}
				{area && error ? <p className={style.error}>{error}</p> : null}
			</div>
			{area ? (
				<FooterBar>
					<ButtonSecondary onClick={onCancel} submittable={!isPending}>
						Cancel
					</ButtonSecondary>
					<ButtonPrimary onClick={onSubmit} submittable={canSubmit}>
						{submitLabel}
					</ButtonPrimary>
				</FooterBar>
			) : null}
		</div>
	)
}
